import React, { useCallback, useMemo, useState } from 'react'
import { Footer } from '~/common/Footer'
import { AppHeader } from '~/common/AppHeader'
import { Button } from 'react-bootstrap'
import { useNavigate, useParams } from 'react-router'
import { useMutation, useQuery } from '@apollo/client'
import {
  AttachSummitChannelToEventDocument,
  GetReleaseLayoutDocument,
  GetSummitLivestreamChannelsDocument,
} from '~/api/generated/graphql'
import '@css/pages/page/SummitLivestreamEvents.scss'
import { asDate } from '~/utils'
import { getEventTimeText } from '~/common/TimeAgo'

const now = new Date()
type SummitEventType = {
  eventId: string
  eventStart?: Date
  eventEnd?: Date
  livestreamChannelId?: string | null
  title?: string | null
}
export const SummitLivestreamEvents = () => {
  const { releaseId } = useParams<{ releaseId: string }>()
  const canEditPosts = true
  const navigate = useNavigate()
  const { data: releaseLayoutData, loading: loadingRelease } = useQuery(GetReleaseLayoutDocument, {
    variables: { id: releaseId ?? '', filter: canEditPosts ? {} : { draft: { eq: false } } },
    skip: !releaseId,
    fetchPolicy: 'cache-and-network',
  })
  const { data, loading: loadingChannels } = useQuery(GetSummitLivestreamChannelsDocument, {
    variables: { id: releaseId ?? '' },
    skip: !releaseId,
    fetchPolicy: 'cache-and-network',
  })
  const [attachSummitChannelToEvent] = useMutation(AttachSummitChannelToEventDocument)
  const channels = data?.release?.channels ?? []
  const channelOptions = [{ channelId: '', name: 'None' }, ...channels] as { channelId: string; name: string }[]
  const release = releaseLayoutData?.release
  const categories = releaseLayoutData?.release?.categories?.edges.map(edge => edge?.node)

  const clickDone = useCallback(() => {
    void navigate(`/summits/${release?.releaseId}`)
  }, [release, navigate])
  const summitChannelIds = channelOptions?.map(channel => channel?.channelId)
  const [savingChannels, setSavingChannels] = useState(false)
  const eventsByTime = useMemo(() => {
    const ret = {} as Record<string, SummitEventType[]>
    const allEvents = [] as SummitEventType[]
    for (const catEdge of releaseLayoutData?.release?.categories?.edges ?? []) {
      for (const eventEdge of catEdge?.node?.events?.edges ?? []) {
        if (eventEdge?.node)
          allEvents.push({
            ...eventEdge?.node,
            eventStart: asDate(eventEdge.node.eventStart),
            eventEnd: asDate(eventEdge.node.eventEnd),
          })
      }
    }
    allEvents.sort((a, b) => {
      const aStart = a.eventStart ?? now
      const bStart = b.eventStart ?? now
      const aEnd = a.eventEnd ?? now
      const bEnd = b.eventEnd ?? now
      if (aStart < bStart || (aStart == bStart && aEnd < bEnd)) {
        return -1
      }
      if (aStart > bStart || (aStart == bStart && aEnd > bEnd)) {
        return 1
      }
      return 0
    })
    for (const event of allEvents) {
      const timeString = getEventTimeText(event.eventStart, event.eventEnd, true)
      if (ret[timeString]) {
        ret[timeString].push(event)
      } else if (event) {
        ret[timeString] = [event]
      }
    }

    return ret
  }, [releaseLayoutData])

  const currentChannels = useMemo(
    () =>
      categories?.reduce(
        (outer, category) =>
          category?.events?.edges?.reduce((acc, edge) => {
            const eventId = edge?.node?.eventId ?? ''
            acc[eventId] = edge?.node?.livestreamChannelId ?? ''
            return acc
          }, outer) ?? outer,
        {} as Record<string, string>
      ),
    [categories]
  )
  const [newChannels, setNewChannels] = useState({ ...currentChannels })
  const handleChangeChanel = useCallback(
    (eventId: string, e: React.ChangeEvent<HTMLSelectElement>) => {
      console.log('changing', eventId, e, e.target.value)
      setNewChannels(oldChannels => {
        return {
          ...oldChannels,
          [eventId]: e.target.value,
        }
      })
    },
    [setNewChannels]
  )
  const [channelChangeStatus, hasChanges] = useMemo(() => {
    if (currentChannels) {
      const newChannelChangeStatus = {} as Record<string, boolean>
      let newHasChanges = false
      for (const [eventId, channelId] of Object.entries(currentChannels)) {
        // If newChannel isn't set, we haven't changed it
        const effectiveChannelId = summitChannelIds?.includes(channelId) ? channelId : ''
        const newChannel = newChannels[eventId] ?? effectiveChannelId
        newChannelChangeStatus[eventId] = effectiveChannelId !== newChannel
        newHasChanges = newHasChanges || newChannelChangeStatus[eventId]
      }
      return [newChannelChangeStatus, newHasChanges]
    } else {
      return [{}, false]
    }
  }, [newChannels, currentChannels, summitChannelIds])
  const clickSave = useCallback(() => {
    setSavingChannels(true)
    const promises = Object.entries(channelChangeStatus)
      .filter(entry => entry[1])
      .map(entry => {
        return attachSummitChannelToEvent({ variables: { eventId: entry[0], channelId: newChannels[entry[0]] } })
      })
    void Promise.all(promises).then(() => {
      setSavingChannels(false)
    })
  }, [setSavingChannels, newChannels, channelChangeStatus, attachSummitChannelToEvent])
  return loadingRelease || (loadingChannels && channels.length === 0) ? (
    <div>Loading...</div>
  ) : (
    <div className={'summit-livestream-events'}>
      <AppHeader />
      <div className={`add-page-wrapper `}>
        <div className={'add-page-container'}>
          <div className={'add-page-header'}>
            <h3 className="comm-title">{release?.title}</h3>
            <div className={'buttons-container'}>
              {hasChanges ? (
                <Button onClick={clickDone} variant="primary">
                  Cancel
                </Button>
              ) : (
                <Button onClick={clickDone} variant="primary">
                  Done
                </Button>
              )}
              {hasChanges && (
                <Button onClick={clickSave} variant="primary">
                  Save Changes
                </Button>
              )}
            </div>
          </div>
          <div>Status: {release?.draft ? 'Draft' : 'Published'}</div>
          <div className={'category-row'}>
            {Object.entries(eventsByTime).map(([timestring, events]) => (
              <div key={timestring}>
                <div className={'category-name'}>{timestring}</div>
                {events.map(event =>
                  event ? (
                    <div
                      key={event.eventId}
                      className={`event-row ${channelChangeStatus[event.eventId] ? 'changed' : ''}`}
                    >
                      <div className={'event-name'}>{event?.title}</div>
                      <div className={'channel-selector'}>
                        <select
                          defaultValue={
                            summitChannelIds?.includes(event?.livestreamChannelId ?? '')
                              ? (event?.livestreamChannelId ?? '')
                              : ''
                          }
                          className={'channel-selector-input'}
                          disabled={savingChannels}
                          onChange={e => handleChangeChanel(event.eventId ?? '', e)}
                        >
                          {channelOptions?.map(channel => (
                            <option key={channel.channelId} value={channel?.channelId}>
                              {channel?.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ) : null
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  )
}
